import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PropertyFilters } from '@/services/types/property';

interface FilterState {
  propertyFilters: PropertyFilters;
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  viewMode: 'grid' | 'list' | 'map';
  savedSearches: SavedSearch[];
}

interface SavedSearch {
  id: string;
  name: string;
  filters: PropertyFilters;
  searchQuery: string;
  createdAt: string;
}

const initialState: FilterState = {
  propertyFilters: {},
  searchQuery: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  viewMode: 'grid',
  savedSearches: [],
};

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setPropertyFilters: (state, action: PayloadAction<PropertyFilters>) => {
      state.propertyFilters = { ...state.propertyFilters, ...action.payload };
    },
    clearPropertyFilters: (state) => {
      state.propertyFilters = {};
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'grid' | 'list' | 'map'>) => {
      state.viewMode = action.payload;
    },
    saveSearch: (state, action: PayloadAction<{ name: string; filters: PropertyFilters; searchQuery: string }>) => {
      const newSearch: SavedSearch = {
        id: Date.now().toString(),
        name: action.payload.name,
        filters: action.payload.filters,
        searchQuery: action.payload.searchQuery,
        createdAt: new Date().toISOString(),
      };
      state.savedSearches.push(newSearch);
    },
    loadSavedSearch: (state, action: PayloadAction<string>) => {
      const search = state.savedSearches.find(s => s.id === action.payload);
      if (search) {
        state.propertyFilters = search.filters;
        state.searchQuery = search.searchQuery;
      }
    },
    deleteSavedSearch: (state, action: PayloadAction<string>) => {
      state.savedSearches = state.savedSearches.filter(s => s.id !== action.payload);
    },
    clearAllFilters: (state) => {
      state.propertyFilters = {};
      state.searchQuery = '';
      state.sortBy = 'createdAt';
      state.sortOrder = 'desc';
    },
  },
});

export const {
  setPropertyFilters,
  clearPropertyFilters,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setViewMode,
  saveSearch,
  loadSavedSearch,
  deleteSavedSearch,
  clearAllFilters,
} = filterSlice.actions;

export default filterSlice.reducer;
